import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import "../employer/css/emp_layout.css";

// Fallback base URL for images if not provided completely by the backend
// const IMAGE_BASE_URL = "http://localhost:4000";
const IMAGE_BASE_URL = window.location.hostname === "localhost" 
  ? "http://localhost:4000" 
  : window.location.origin;

export default function JSMarketplaceListingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // App Mechanics States
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [data, setData] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  
  // Context Interactive Fields
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [visitorMessage, setVisitorMessage] = useState("");

  // ... your state hooks stay here ...

// 1. Wrap the navigation failure fallback in useCallback
const handleFailureNavigation = useCallback(() => {
  if (window.history.length > 1) {
    navigate(-1);
  } else {
    navigate("/marketplace");
  }
}, [navigate]);

// 2. Add handleFailureNavigation to the fetchDetails dependency array
const fetchDetails = useCallback(async () => {
  const token = localStorage.getItem("token");
  if (!token) { navigate("/signin"); return; }

  try {
    const response = await fetch(`${API_ENDPOINTS.MARKETPLACE}/listings/${id}/details`, {
      headers: { "Authorization": `Bearer ${token}` }
    });
    if (response.ok) {
      const resData = await response.json();
      setData(resData);
      setEditForm(resData.listing);
      setLoading(false);
    } else {
      alert("Could not load marketplace details.");
      handleFailureNavigation();
    }
  } catch (err) {
    console.error("Error pulling listing item details:", err);
    alert("An unexpected error occurred while fetching details.");
    handleFailureNavigation();
  }
}, [id, navigate, handleFailureNavigation]); // Added safely here

  // Helper function to resolve image paths correctly
  // const resolveImageUrl = (url) => {
  //   if (!url) return "";
  //   if (url.startsWith("http://") || url.startsWith("https://")) return url;
  //   return `${IMAGE_BASE_URL}${url}`;
  // };

  const resolveImageUrl = (url) => {
    if (!url) return "";
    // If the backend accidentally sends a full third-party URL, use it as-is
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    
    // Ensures there is a single clean slash between host and path
    const sanitizedUrl = url.startsWith("/") ? url : `/${url}`;
    return `${IMAGE_BASE_URL}${sanitizedUrl}`;
  };

  
useEffect(() => {
  fetchDetails();
}, [fetchDetails]); // Now fetchDetails is a safe dependency!

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateListing = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      setUpdating(true);
      const response = await fetch(`${API_ENDPOINTS.MARKETPLACE}/listings/${id}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description,
          price: parseFloat(editForm.price),
          stockQuantity: editForm.listingType === "Product" ? parseInt(editForm.stockQuantity) : null,
          rentalFrequency: editForm.listingType === "RentalSpace" ? editForm.rentalFrequency : null
        })
      });

      if (response.ok) {
        alert("Listing updated successfully! 🎉");
        setIsEditing(false);
        fetchDetails(); 
      } else {
        alert("Failed to modify catalog data.");
      }
    } catch (err) {
      console.error("Update crash exception:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      setSendingRequest(true);
      const response = await fetch(`${API_ENDPOINTS.MARKETPLACE}/listings/${id}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ message: visitorMessage })
      });

      if (response.ok) {
        alert("Inquiry successfully transmitted! The listing owner has been notified. ✉️");
        setVisitorMessage("");
      } else {
        alert("Failed to submit connection inquiry.");
      }
    } catch (err) {
      console.error("Request generation failure:", err);
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading || !data) {
    return (
      <JSLayout title="Loading Details..." titleEm="Please wait ◈">
        <div className="emd-loading-container"><div className="si-spinner"></div></div>
      </JSLayout>
    );
  }

  const { listing, requests = [] } = data;
  const hasImages = listing.images && listing.images.length > 0;

  return (
    <JSLayout title={listing.isOwner ? "Manage Your" : "Marketplace"} titleEm="Showroom Hub ◈">
      <div className="emd-fade" style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
        
        {/* Navigation Action Strip */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button className="emp-btn-ghost" onClick={() => navigate(-1)}>
            ← Go Back
          </button>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            {listing.isOwner && (
              <button 
                className="emp-btn-secondary" 
                style={{ padding: "0.4rem 1rem", fontSize: "0.85rem" }}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? "Cancel Modifications" : "📝 Edit Details"}
              </button>
            )}
            <span style={{ color: listing.isActive ? "#4BB543" : "#ff4444", fontWeight: "600", fontSize: "0.9rem" }}>
              ● {listing.isActive ? "Active Listing" : "Archived"}
            </span>
          </div>
        </div>

        {/* Dynamic Dual Context Columns */}
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "2rem", alignItems: "start" }}>
          
          {/* LEFT PANEL: Media Visuals & Content Data Forms */}
          <div className="emd-panel-card" style={{ padding: "1rem" }}>
            
            {/* Gallery Frame */}
            <div style={{ width: "100%", marginBottom: "1.5rem" }}>
              <div style={{ width: "100%", height: "350px", borderRadius: "8px", overflow: "hidden", background: "#1a0f1d", border: "1px solid #332238" }}>
                {hasImages ? (
                  <img 
                    src={resolveImageUrl(listing.images[activeImageIdx])} 
                    alt={listing.title} 
                    style={{ width: "100%", height: "100%", objectFit: "contain" }} 
                  />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#666" }}>
                    <span style={{ fontSize: "4rem" }}>{listing.listingType === "RentalSpace" ? "🏢" : "🧴"}</span>
                    <span>No photos attached</span>
                  </div>
                )}
              </div>
              
              {hasImages && listing.images.length > 1 && (
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", overflowX: "auto" }}>
                  {listing.images.map((imgUrl, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => setActiveImageIdx(idx)}
                      style={{ 
                        width: "65px", height: "65px", borderRadius: "4px", overflow: "hidden", cursor: "pointer",
                        border: activeImageIdx === idx ? "2px solid #d4af37" : "1px solid #444", opacity: activeImageIdx === idx ? 1 : 0.6,
                        flexShrink: 0
                      }}
                    >
                      <img 
                        src={resolveImageUrl(imgUrl)} 
                        alt="Thumbnail preview" 
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DYNAMIC VIEW/EDIT MODE CONTEXT PANEL */}
            {!isEditing ? (
              <>
                <span style={{ background: "#FFE8B4", color: "#aa7c11", padding: "0.25rem 1rem", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "bold" }}>
                  {listing.category}
                </span>
                <h1 style={{ color: "#4B4B4B", fontSize: "1.8rem", margin: "0.75rem 0 0.5rem 0", fontWeight: "600" }}>{listing.title}</h1>
                
                <div style={{ display: "flex", gap: "1.5rem", margin: "0rem 0", color: "#4B4B4B", fontSize: "0.9rem" }}>
                  <div>📍 {listing.locationCity || "Available Countrywide"}</div>
                  {listing.listingType === "Product" ? (
                    <div>📦 Stock Available: <strong style={{ color: "#4B4B4B" }}>{listing.stockQuantity ?? "N/A"} items</strong></div>
                  ) : (
                    <div>🗓️ Available starting: <strong style={{ color: "#4B4B4B" }}>{listing.availableFrom ?? "Immediate"}</strong></div>
                  )}
                </div>
                <div className="emd-listing-divider" />
                <h3 style={{ color: "#4B4B4B", fontSize: "0.95rem", marginBottom: "0.5rem" }}>Specification Details</h3>
                <p style={{ color: "#4B4B4B", lineHeight: "1.6", fontSize: "0.95rem", whiteSpace: "pre-line" }}>{listing.description}</p>

                <div style={{ marginTop: "2rem", padding: "1.25rem", background: "rgba(212, 175, 55, 0.05)", borderRadius: "8px", border: "1px solid rgba(212, 175, 55, 0.2)" }}>
                  <span style={{ display: "block", color: "#4B4B4B", fontSize: "0.85rem" }}>Financial Layout Plan</span>
                  <span style={{ fontSize: "1.75rem", fontWeight: "bold", color: "#d4af37" }}>KES {listing.price?.toLocaleString()}</span>
                  {listing.listingType === "RentalSpace" && <span style={{ color: "#b3a1b8", fontSize: "0.9rem" }}> / Billed {listing.rentalFrequency}</span>}
                </div>
              </>
            ) : (
              /* OWNER EDIT FORM VIEW LAYER */
              <form onSubmit={handleUpdateListing} style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
                <h3 style={{ color: "#d4af37", margin: "0 0 0.5rem 0" }}>Modify Showroom Content</h3>
                
                <div>
                  <label className="emd-stat-label">Listing Title</label>
                  <input type="text" name="title" value={editForm.title || ""} onChange={handleEditChange} required className="emp-input" />
                </div>

                <div>
                  <label className="emd-stat-label">Showroom Specification Text</label>
                  <textarea name="description" value={editForm.description || ""} onChange={handleEditChange} rows="4" required className="emp-textarea" />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div>
                    <label className="emd-stat-label">Price / Rent Cost (KES)</label>
                    <input type="number" name="price" value={editForm.price || ""} onChange={handleEditChange} required className="emp-input" />
                  </div>
                  
                  {listing.listingType === "Product" ? (
                    <div>
                      <label className="emd-stat-label">Available Physical Stock</label>
                      <input type="number" name="stockQuantity" value={editForm.stockQuantity || ""} onChange={handleEditChange} required className="emp-input" />
                    </div>
                  ) : (
                    <div>
                      <label className="emd-stat-label">Lease Frequency</label>
                      <select name="rentalFrequency" value={editForm.rentalFrequency || "Monthly"} onChange={handleEditChange} className="emp-select">
                        <option value="Hourly">Hourly Rate</option>
                        <option value="Daily">Daily Rate</option>
                        <option value="Monthly">Monthly Lease</option>
                      </select>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={updating} className="emp-btn-primary" style={{ marginTop: "1rem", background: "linear-gradient(135deg, #4BB543 0%, #2e7d32 100%)", border: "none" }}>
                  {updating ? "Saving Records..." : "Save Changes ✔"}
                </button>
              </form>
            )}

          </div>

          {/* RIGHT PANEL: Role-Based Dynamic Logic Layout */}
          <div>
            {listing.isOwner ? (
              /* MODE A: OWNER VIEW - Inbound Requests Activity Feed */
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <h2 style={{ color: "#4B4B4B", fontSize: "1.3rem", fontWeight: "600", paddingLeft: "5px" }}>
                  Inbound Inquiries ({requests.length})
                </h2>

                {requests.length === 0 ? (
                  <div className="emd-panel-card" style={{ padding: "3rem", textAlign: "center", color: "#666" }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>✉️</div>
                    <h4 style={{ color: "#4B4B4B" }}>No requests logged yet</h4>
                    <p style={{ fontSize: "0.85rem", maxWidth: "250px", margin: "5px auto 0 auto" }}>When professionals look to connect, their details populate here.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {requests.map((req) => (
                      <div key={req.id} className="emd-panel-card" style={{ padding: "1.25rem", borderLeft: "4px solid #d4af37" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "0.75rem" }}>
                          <div>
                            <h4 style={{ color: "#4B4B4B", margin: 0, fontSize: "1rem" }}>{req.requesterName}</h4>
                            <span style={{ fontSize: "0.75rem", color: "#888" }}>
                              {new Date(req.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        {req.notes && (
                          <p style={{ color: "#4B4B4B", fontSize: "0.9rem", background: "#FFF5D5", padding: "0.75rem", borderRadius: "6px", margin: "0 0 0.25rem 0" }}>
                            "{req.notes}"
                          </p>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", background: "rgba(255,255,255,0.02)", padding: "0.1rem 0.1rem", borderRadius: "4px" }}>
                          <div style={{ fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                            📧 <a href={`mailto:${req.requesterEmail}`} style={{ color: "#6D2277", textDecoration: "none" }}>{req.requesterEmail}</a>
                          </div>
                          <div style={{ fontSize: "0.8rem" }}>
                            📞 <a href={`tel:${req.requesterPhone}`} style={{ color: "#6D2277", textDecoration: "none" }}>{req.requesterPhone}</a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* MODE B: VISITOR VIEW - Direct Inbound Connection Message Panel Form */
              <div className="emd-panel-card" style={{ padding: "2rem", position: "sticky", top: "20px" }}>
                <h3 style={{ color: "#4B4B4B", fontSize: "1.25rem", fontWeight: "600", margin: "0 0 0.5rem 0" }}>Interested in this asset?</h3>
                <p style={{ color: "#4B4B4B", fontSize: "0.85rem", marginBottom: "1.5rem", lineHeight: "1.4" }}>
                  Submit an inquiry below. Your verified platform registration profile email and mobile details will be securely shared with the supplier immediately to handle logistics.
                </p>

                <form onSubmit={handleSendRequest} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div>
                    <label className="emd-stat-label">Inquiry Message</label>
                    <textarea 
                      value={visitorMessage} 
                      onChange={(e) => setVisitorMessage(e.target.value)}
                      required 
                      rows="5"
                      placeholder="e.g., Hi, I am highly interested in purchasing this stock lot / leasing this workspace block. Let's arrange a callback timeline..."
                      className="emp-textarea"
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={sendingRequest || !visitorMessage.trim()} 
                    className="emp-btn-primary" 
                    style={{ width: "100%", background: "linear-gradient(135deg, #d4af37 0%, #aa7c11 100%)", border: "none" }}
                  >
                    {sendingRequest ? "Sending Message..." : "Submit Inquiry Request 🚀"}
                  </button>
                </form>
              </div>
            )}
          </div>

        </div>
      </div>
    </JSLayout>
  );
}